import type { SchemaInput } from '../shared/schema-input.js';
import { isNumber, isString } from '../shared/runtime-values.js';
import { array, object, optional, unknown as unknownSchema } from 'zod/mini';
import { GeocoderError } from './geocoder-error.js';
import { buildGeocoderPresentation } from '../shared/geocoder-presentation.js';
import { roundGeocoderLocationBias } from '../shared/geocoder-location-bias.js';

export interface NormalizedFeature {
  properties: {
    lat: number;
    lon: number;
    formatted: string;
    type?: string;
    category?: string;
    street?: string;
    houseNumber?: string;
    city?: string;
    region?: string;
    country?: string;
    postcode?: string;
    countryCode?: string;
    osmType?: string;
    osmId?: string | number;
    importance?: number;
    placeRank?: number;
    extent?: [number, number, number, number] /* [minLon, maxLat, maxLon, minLat] */;
  };
  autocompletePresentation?: { primary: string; secondary?: string };
  geocoderProvider?: 'photon' | 'nominatim' | 'geoapify';
  geocoderFallbackFrom?: 'photon' | 'nominatim' | 'geoapify';
  geocoderFallbackTrigger?: 'network' | 'http' | 'parse' | 'empty_results';
}

export interface PhotonSearchOptions {
  lat?: number;
  lon?: number;
  osm_tag?: string;
  lang?: string;
  signal?: AbortSignal;
}

const photonPropertiesSchema = object({
  name: optional(unknownSchema()),
  street: optional(unknownSchema()),
  housenumber: optional(unknownSchema()),
  city: optional(unknownSchema()),
  district: optional(unknownSchema()),
  county: optional(unknownSchema()),
  state: optional(unknownSchema()),
  country: optional(unknownSchema()),
  postcode: optional(unknownSchema()),
  countrycode: optional(unknownSchema()),
  osm_key: optional(unknownSchema()),
  osm_value: optional(unknownSchema()),
  osm_type: optional(unknownSchema()),
  osm_id: optional(unknownSchema()),
  type: optional(unknownSchema()),
  extent: optional(unknownSchema()),
});

const photonFeatureSchema = object({
  geometry: object({ coordinates: array(unknownSchema()) }),
  properties: photonPropertiesSchema,
});

const photonResponseSchema = object({
  features: array(unknownSchema()),
});

function stringValue(value: SchemaInput): string | undefined {
  return isString(value) && value.length > 0 ? value : undefined;
}

function parseExtent(value: SchemaInput): [number, number, number, number] | undefined {
  if (!Array.isArray(value) || value.length !== 4) return undefined;
  const extent = value.map(Number);
  // SAFETY: The local owner contract defines this value with the stated concrete type.
  return extent.every(Number.isFinite) ? extent as [number, number, number, number] : undefined;
}

function normalize(feature: SchemaInput): NormalizedFeature | null {
  const result = photonFeatureSchema.safeParse(feature);
  if (!result.success) return null;
  const { geometry, properties } = result.data;
  const coordinates = geometry.coordinates;
  if (coordinates.length < 2) return null;
  const lon = Number(coordinates[0]);
  const lat = Number(coordinates[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  const presentation = buildGeocoderPresentation(properties);
  if (!presentation) return null;
  const type = stringValue(properties.type);
  const category = stringValue(properties.osm_value) ?? stringValue(properties.osm_key);
  const postcode = stringValue(properties.postcode);
  const countryCode = stringValue(properties.countrycode);
  const osmType = stringValue(properties.osm_type);
  const osmId = isString(properties.osm_id) || isNumber(properties.osm_id)
    ? properties.osm_id
    : undefined;
  const extent = parseExtent(properties.extent);
  const normalizedProperties: NormalizedFeature['properties'] = {
    lat,
    lon,
    formatted: presentation.formatted,
    type,
    extent,
  };
  Object.assign(normalizedProperties, Object.fromEntries([
    ['category', category],
    ['street', stringValue(properties.street)],
    ['houseNumber', stringValue(properties.housenumber)],
    ['city', stringValue(properties.city)],
    ['region', stringValue(properties.state)],
    ['country', stringValue(properties.country)],
    ['postcode', postcode],
    ['countryCode', countryCode],
    ['osmType', osmType],
    ['osmId', osmId],
  ].filter((entry) => entry[1] !== undefined)));
  return {
    properties: normalizedProperties,
    autocompletePresentation: {
      primary: presentation.primary,
      secondary: presentation.secondary,
    },
  };
}

// The hosted Photon API rejects unsupported lang values such as "zh".
// Omit unsupported locales so searches can still match multilingual OSM names.
const PHOTON_LANGUAGES = new Set(['default', 'de', 'en', 'fr']);

export async function photonSearch(query: string, limit = 5, options?: PhotonSearchOptions): Promise<NormalizedFeature[]> {
  const params = new URLSearchParams();
  params.set('q', query);
  params.set('limit', String(limit));
  params.set('location_bias_scale', '0.5');
  if (options?.lat !== undefined && options?.lon !== undefined) {
    const bias = roundGeocoderLocationBias(options.lat, options.lon);
    params.set('lat', String(bias.lat));
    params.set('lon', String(bias.lon));
  }
  if (options?.osm_tag) {
    params.set('osm_tag', options.osm_tag);
  }
  if (options?.lang && PHOTON_LANGUAGES.has(options.lang)) {
    params.set('lang', options.lang);
  }
  const origin = !('window' in globalThis) ? 'https://drawonamap.com' : window.location.origin;
  const url = new URL('/api/geocode', origin);
  url.searchParams.set('provider', 'photon');
  for (const [key, value] of params) url.searchParams.set(key, value);
  const res = await fetch(url, { signal: options?.signal });
  if (!res.ok) {
    throw new GeocoderError(`Geocode failed: ${res.status}`, 'http', { response: res });
  }
  const data = await res.json();
  const features = normalizePhotonResponse(data);
  if (res.headers.get('x-geocoder-provider') !== 'geoapify') return features;
  return features.map((feature) => ({ ...feature, geocoderProvider: 'geoapify', geocoderFallbackFrom: 'photon', geocoderFallbackTrigger: 'http' }));
}

function normalizePhotonResponse(data: SchemaInput): NormalizedFeature[] {
  const result = photonResponseSchema.safeParse(data);
  if (!result.success) {
    throw new GeocoderError('Photon response did not contain a feature list', 'parse');
  }
  return result.data.features.map(normalize).filter((feature): feature is NormalizedFeature => !!feature);
}

export async function photonReverse(
  lat: number,
  lon: number,
  options?: Pick<PhotonSearchOptions, 'lang' | 'signal'>,
): Promise<NormalizedFeature[]> {
  const origin = !('window' in globalThis) ? 'https://drawonamap.com' : window.location.origin;
  const url = new URL('/api/geocode', origin);
  url.searchParams.set('provider', 'photon');
  url.searchParams.set('mode', 'reverse');
  url.searchParams.set('lat', String(lat));
  url.searchParams.set('lon', String(lon));
  if (options?.lang && PHOTON_LANGUAGES.has(options.lang)) url.searchParams.set('lang', options.lang);
  const res = await fetch(url, { signal: options?.signal });
  if (!res.ok) {
    throw new GeocoderError(`Reverse geocode failed: ${res.status}`, 'http', { response: res });
  }
  return normalizePhotonResponse(await res.json());
}
